// src/store/slices/allergiesSlice.ts

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getAllergicSectionsApi,
  postAllergicSectionsApi,
  putAllergicSectionsApi,
  getAllergiesApi,
  postAllergiesApi,
  putAllergiesApi,
  getLocalizedSectionsApi,
  postLocalizedAllergySectionApi,
  deleteLocalizedAllergySectionApi,
  postLocalizedAllergyItemApi,
  getLocalizedAllergyItemApi,
  deleteLocalizedAllergyItemApi,
  getRawMaterialsApi,
  getAssignedRawMaterials,
  postAssignRawMaterialsApi,
} from "app/services/allergies";
import {
  AllergySectionsItem,
  AllergyItem,
  PostAllergyItemPayload,
  PostAllergicSectionPayload,
  localizedItem,
  PostLocalizedAllergySectionPayload,
  RawMaterial,
  AssignedRawMaterial,
  PostAssignRawMaterialPayload,
} from "app/types/allergies";
import { PaginationInterfaceLF } from "app/types";
import { AppDispatch } from "../store";
import { errorHandler } from "app/shared/Service/errorHandler";
import { alertMessage } from "../actions/common";
import { createQuery } from "app/utils/constants/common";
import { RootState } from "../reducers/hooks";

interface AllergyFilters {
  search?: string;
}

interface InitialState {
  allergicSections: AllergySectionsItem[];
  allergyItems: AllergyItem[];
  allergicSectionsPagination: PaginationInterfaceLF | null;
  allergyItemsPagination: PaginationInterfaceLF | null;
  isAllergiesLoading: boolean;
  allergyFilters: AllergyFilters | null;
  itemsPerPage: number;
  localizedAllergySections: localizedItem[];
  localizedAllergySectionsPagination: PaginationInterfaceLF | null;
  localizedAllergyItems: localizedItem[];
  localizedAllergyItemsPagination: PaginationInterfaceLF | null;
  rawMaterialsList: RawMaterial[];
  rawMaterialsListPagination: PaginationInterfaceLF | null;
  rawMaterialsRowsPerPage: number;
  assignedRawMaterials: AssignedRawMaterial[];
  allergiesSectionRowsPerPage: number;
  allergyListRowsPerPage: number;
  localizedAllergiesListRowsPerPage: number;
  localizedAllergiesSectionsListRowsPerPage: number;
}

const initialState: InitialState = {
  allergicSections: [],
  allergyItems: [],
  allergicSectionsPagination: null,
  allergyItemsPagination: null,
  isAllergiesLoading: false,
  allergyFilters: null,
  itemsPerPage: 20,
  localizedAllergySections: [],
  localizedAllergySectionsPagination: null,
  localizedAllergyItems: [],
  localizedAllergyItemsPagination: null,
  rawMaterialsList: [],
  rawMaterialsListPagination: null,
  rawMaterialsRowsPerPage: 50,
  assignedRawMaterials: [],
  allergiesSectionRowsPerPage: 20,
  allergyListRowsPerPage: 20,
  localizedAllergiesListRowsPerPage: 20,
  localizedAllergiesSectionsListRowsPerPage: 20,
};

export const allergiesSlice = createSlice({
  name: "allergies",
  initialState,
  reducers: {
    setAllergicSections: (
      state,
      action: PayloadAction<{
        data: AllergySectionsItem[];
        pagination: PaginationInterfaceLF;
      }>
    ) => {
      state.allergicSections = action.payload.data;
      state.allergicSectionsPagination = action.payload.pagination;
    },
    setLocalizedAllergicSections: (
      state,
      action: PayloadAction<{
        data: localizedItem[];
        pagination: PaginationInterfaceLF;
      }>
    ) => {
      state.localizedAllergySections = action.payload.data;
      state.localizedAllergySectionsPagination = action.payload.pagination;
    },

    setAllergyItems: (
      state,
      action: PayloadAction<{
        data: AllergyItem[];
        pagination: PaginationInterfaceLF;
      }>
    ) => {
      state.allergyItems = action.payload.data;
      state.allergyItemsPagination = action.payload.pagination;
    },
    setLocalizedAllergicItems: (
      state,
      action: PayloadAction<{
        data: localizedItem[];
        pagination: PaginationInterfaceLF;
      }>
    ) => {
      state.localizedAllergyItems = action.payload.data;
      state.localizedAllergyItemsPagination = action.payload.pagination;
    },
    setIsAllergiesLoading: (state, action: PayloadAction<boolean>) => {
      state.isAllergiesLoading = action.payload;
    },

    setAllergyFilters: (
      state,
      action: PayloadAction<AllergyFilters | null>
    ) => {
      state.allergyFilters = action.payload;
    },

    setItemsPerPage: (state, action: PayloadAction<number>) => {
      state.itemsPerPage = action.payload;
    },
    setRawMaterialsList: (
      state,
      action: PayloadAction<{
        data: RawMaterial[];
        pagination: PaginationInterfaceLF;
      }>
    ) => {
      state.rawMaterialsList = action.payload.data;
      state.rawMaterialsListPagination = action.payload.pagination;
    },
    setAssignedRawMaterials: (
      state,
      action: PayloadAction<AssignedRawMaterial[]>
    ) => {
      state.assignedRawMaterials = action.payload;
    },
    setAllergySectionsListRowsPerPage: (
      state,
      action: PayloadAction<number>
    ) => {
      state.allergiesSectionRowsPerPage = action.payload;
    },
    setAllergyListRowsPerPage: (state, action: PayloadAction<number>) => {
      state.allergyListRowsPerPage = action.payload;
    },
    setLocalizedSectionsListRowsPerPage: (
      state,
      action: PayloadAction<number>
    ) => {
      state.localizedAllergiesSectionsListRowsPerPage = action.payload;
    },
    setLocalizedAllergyListRowsPerPage: (
      state,
      action: PayloadAction<number>
    ) => {
      state.localizedAllergiesListRowsPerPage = action.payload;
    },
    setRawMaterialsRowsPerPage: (state, action: PayloadAction<number>) => {
      state.rawMaterialsRowsPerPage = action.payload;
    },
    updateAllergicSection: (
      state,
      action: PayloadAction<AllergySectionsItem>
    ) => {
      state.allergicSections = state.allergicSections.map((section) =>
        section.id === action.payload.id ? action.payload : section
      );
    },

    updateAllergyItem: (state, action: PayloadAction<AllergyItem>) => {
      state.allergyItems = state.allergyItems.map((item) =>
        item.id === action.payload.id ? action.payload : item
      );
    },
  },
});

export const {
  setAllergicSections,
  setAllergyItems,
  setIsAllergiesLoading,
  setAllergyFilters,
  setItemsPerPage,
  setLocalizedAllergicSections,
  setLocalizedAllergicItems,
  setRawMaterialsList,
  setAssignedRawMaterials,
  setAllergySectionsListRowsPerPage,
  setAllergyListRowsPerPage,
  setLocalizedAllergyListRowsPerPage,
  setLocalizedSectionsListRowsPerPage,
  setRawMaterialsRowsPerPage,
  updateAllergicSection,
  updateAllergyItem,
} = allergiesSlice.actions;

export default allergiesSlice.reducer;
// Fetch all allergic sections with optional query
export const getAllergicSectionsAction = ({
  continuationToken = null,
}: {
  continuationToken: string | null;
}) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const { allergiesSectionRowsPerPage } = getState().allergies;
    try {
      const query =
        "?" +
        createQuery({
          filter: {
            limit: allergiesSectionRowsPerPage,
            continutationToken: continuationToken,
          },
        });
      dispatch(setIsAllergiesLoading(true));
      const { data } = await getAllergicSectionsApi(query);
      dispatch(
        setAllergicSections({
          data: data.data,
          pagination: data.pagination,
        })
      );
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    } finally {
      dispatch(setIsAllergiesLoading(false));
    }
  };
};
type LocalizedAllergicSectionsParams = {
  sectionId: number;
  continuationToken?: string | null;
};
export const getLocalizedAllergicSectionsAction = ({
  sectionId,
  continuationToken,
}: LocalizedAllergicSectionsParams) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { localizedAllergiesSectionsListRowsPerPage } =
        getState().allergies;
      let query = "";
      query =
        "?" +
        createQuery({
          filter: {
            limit: localizedAllergiesSectionsListRowsPerPage,
            continutationToken: continuationToken,
          },
        });
      const { data } = await getLocalizedSectionsApi(sectionId, query);
      dispatch(
        setLocalizedAllergicSections({
          data: data.data,
          pagination: data.pagination,
        })
      );
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

// Create a new allergic section
export const postAllergicSectionAction = (
  payload: PostAllergicSectionPayload
) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await postAllergicSectionsApi(payload);
      dispatch(alertMessage("Allergic section added successfully", "success"));
      dispatch(getAllergicSectionsAction({ continuationToken: null }));

      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

// Update an existing allergic section
export const putAllergicSectionAction = (
  id: number,
  payload: PostAllergicSectionPayload
) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await putAllergicSectionsApi(id, payload);
      if (data?.data) {
        dispatch(updateAllergicSection(data.data));
      }
      dispatch(
        alertMessage("Allergic section updated successfully", "success")
      );
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getAllergyItemsAction = ({
  sectionId,
  continuationToken = null,
}: {
  sectionId: number;
  continuationToken?: string | null;
}) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      dispatch(setIsAllergiesLoading(true));

      const { allergyListRowsPerPage } = getState().allergies;

      const query =
        "?" +
        createQuery({
          filter: {
            limit: allergyListRowsPerPage,
            continutationToken: continuationToken,
            allergicSectionId: sectionId,
          },
        });

      const { data } = await getAllergiesApi(query);

      dispatch(
        setAllergyItems({
          data: data.data,
          pagination: data.pagination,
        })
      );
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    } finally {
      dispatch(setIsAllergiesLoading(false));
    }
  };
};

interface LocalizedAllergiesParams {
  itemId: number;
  continuationToken?: string | null;
}
export const getLocalizedAllergicItemsAction = ({
  itemId,
  continuationToken,
}: LocalizedAllergiesParams) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { localizedAllergiesListRowsPerPage } = getState().allergies;
      let query = "";
      query =
        "?" +
        createQuery({
          filter: {
            limit: localizedAllergiesListRowsPerPage,
            continutationToken: continuationToken,
          },
        });
      const { data } = await getLocalizedAllergyItemApi(itemId, query);
      dispatch(
        setLocalizedAllergicItems({
          data: data.data,
          pagination: data.pagination,
        })
      );
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

// Add a new allergy item
export const postAllergyItemAction = (payload: PostAllergyItemPayload) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await postAllergiesApi(payload);
      dispatch(getAllergyItemsAction({ sectionId: payload.allergicSectionId }));
      dispatch(alertMessage("Allergy item added successfully", "success"));
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

// Update an existing allergy item
export const putAllergyItemAction = (
  id: number,
  payload: PostAllergyItemPayload
) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await putAllergiesApi(id, payload);
      if (data?.data) {
        dispatch(updateAllergyItem(data.data));
      }
      dispatch(alertMessage("Allergy item updated successfully", "success"));
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const postLocalizedSectionAction = (
  allergySectionId: number,
  payload: PostLocalizedAllergySectionPayload
) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await postLocalizedAllergySectionApi(
        allergySectionId,
        payload
      );
      dispatch(alertMessage("Added successfully", "success"));
      dispatch(
        getLocalizedAllergicSectionsAction({ sectionId: allergySectionId })
      );
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const deleteLocalizedSectionAction = (
  allergySectionId: number,
  loacle: string
) => {
  return async (dispatch: AppDispatch) => {
    try {
      const query =
        "?" +
        createQuery({
          filter: {
            locale: loacle,
          },
        });
      const { data } = await deleteLocalizedAllergySectionApi(
        allergySectionId,
        query
      );
      dispatch(alertMessage("Deleted successfully", "success"));
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const postLocalizedAllergyItemsAction = (
  allergyItemId: number,
  payload: PostLocalizedAllergySectionPayload
) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await postLocalizedAllergyItemApi(
        allergyItemId,
        payload
      );
      dispatch(alertMessage("Added successfully", "success"));
      dispatch(getLocalizedAllergicItemsAction({ itemId: allergyItemId }));

      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const deleteLocalizedAllergyItemAction = (
  allergyItemId: number,
  loacle: string
) => {
  return async (dispatch: AppDispatch) => {
    try {
      const query =
        "?" +
        createQuery({
          filter: {
            locale: loacle,
          },
        });
      const { data } = await deleteLocalizedAllergyItemApi(
        allergyItemId,
        query
      );
      dispatch(alertMessage("Deleted successfully", "success"));
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getRawMaterialsListAction = ({
  continuationToken = null,
  search = "",
}: {
  continuationToken?: string | null;
  search?: string;
}) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const { rawMaterialsRowsPerPage } = getState().allergies;

    try {
      const query =
        "?" +
        createQuery({
          filter: {
            limit: rawMaterialsRowsPerPage,
            continutationToken: continuationToken,
            ...(search ? { search } : {}),
          },
        });

      const { data } = await getRawMaterialsApi(query);

      dispatch(
        setRawMaterialsList({
          data: data.data,
          pagination: data.pagination,
        })
      );
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getAssignedRawMaterialsListAction = (id: number) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await getAssignedRawMaterials(id);
      dispatch(setAssignedRawMaterials(data.data));
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const postAssignRawMaterialsAction = (
  id: number,
  payload: PostAssignRawMaterialPayload
) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await postAssignRawMaterialsApi(id, payload);
      dispatch(getAssignedRawMaterialsListAction(id));
      dispatch(alertMessage("Assigned successfully", "success"));
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};
